import React, { useState, useCallback } from 'react';
import { WebSocketAdapter } from '../utils/WebSocketAdapter';
import { type PaidRoomEntry, type PremiumPurchase } from '../types';
import './PromoRoomsPage.css';

type PromoGamePack = 'blitz' | 'team' | 'family' | 'party' | 'all';

type PromoTemplateOption = {
  value: string;
  label: string;
  description: string;
};

const GAME_PACK_OPTIONS: { value: PromoGamePack; label: string }[] = [
  { value: 'all', label: 'All packs' },
  { value: 'blitz', label: 'Blitz' },
  { value: 'team', label: 'Team' },
  { value: 'family', label: 'Family' },
  { value: 'party', label: 'Party' },
];

const DURATION_PRESETS = [7, 14, 30];

function toDateInputValue(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD as local midnight. */
function parseLocalDateStart(dateStr: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt.getTime();
}

function formatPromoDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const PROMO_TEMPLATE_OPTIONS: PromoTemplateOption[] = [
  {
    value: 'promo-room-code-with-how-to',
    label: 'How-to template',
    description: 'Default: includes image + how it works steps',
  },
  {
    value: 'ed52bae0-84cf-4af8-a5db-9f0f4e35b84c',
    label: 'Simple promo template',
    description: 'Original short promo email',
  },
];

const DEFAULT_PROMO_TEMPLATE = PROMO_TEMPLATE_OPTIONS[0].value;

interface EmailTarget {
  roomName: string;
  games?: string[];
}

interface PromoRoomsPageProps {
  socket: WebSocketAdapter | null;
  paidRooms: PaidRoomEntry[];
  onRefresh: () => void;
  onRoomClick: (entry: PaidRoomEntry) => void;
}

const PromoRoomsPage: React.FC<PromoRoomsPageProps> = ({
  socket,
  paidRooms,
  onRefresh,
  onRoomClick,
}) => {
  const [code, setCode] = useState('');
  const [startDate, setStartDate] = useState(toDateInputValue());
  const [durationDays, setDurationDays] = useState<number>(7);
  const [customDuration, setCustomDuration] = useState('');
  const [gamePack, setGamePack] = useState<PromoGamePack>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeCheckResult, setCodeCheckResult] = useState<'available' | 'taken' | 'invalid' | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [emailTarget, setEmailTarget] = useState<EmailTarget | null>(null);
  const [emailTo, setEmailTo] = useState('');
  const [firstName, setFirstName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_PROMO_TEMPLATE);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const promoRooms = paidRooms.filter((entry) => entry.purchase.sessionId?.startsWith('promo-'));

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const openEmailModal = useCallback((target: EmailTarget) => {
    setEmailTarget(target);
    setEmailTo('');
    setFirstName('');
    setSelectedTemplate(DEFAULT_PROMO_TEMPLATE);
  }, []);

  const closeEmailModal = useCallback(() => {
    if (isSendingEmail) return;
    setEmailTarget(null);
    setEmailTo('');
    setFirstName('');
    setSelectedTemplate(DEFAULT_PROMO_TEMPLATE);
  }, [isSendingEmail]);

  const effectiveDuration = customDuration ? parseInt(customDuration, 10) : durationDays;
  const isValidDuration =
    !customDuration || (Number.isInteger(effectiveDuration) && effectiveDuration >= 1 && effectiveDuration <= 90);
  const parsedStartMs = parseLocalDateStart(startDate);
  const previewExpiresAt =
    parsedStartMs != null && isValidDuration && effectiveDuration >= 1
      ? parsedStartMs + effectiveDuration * 24 * 60 * 60 * 1000
      : null;

  const handleGenerateRandomCode = useCallback(() => {
    if (!socket?.connected) {
      showToast('error', 'Not connected to server');
      return;
    }
    setIsGeneratingCode(true);
    setCodeCheckResult(null);
    socket.emit('admin-get-random-promo-code', {});

    const handler = (data: { code?: string; error?: string }) => {
      setIsGeneratingCode(false);
      socket?.off('admin-get-random-promo-code-result', handler);
      if (data.code) {
        setCode(data.code);
        setCodeCheckResult('available');
      } else {
        showToast('error', data.error || 'Failed to generate code');
      }
    };
    socket.on('admin-get-random-promo-code-result', handler);
  }, [socket, showToast]);

  const handleCheckCode = useCallback(() => {
    const normalized = code.trim().toUpperCase();
    if (normalized.length !== 4) {
      setCodeCheckResult('invalid');
      return;
    }
    if (!socket?.connected) {
      showToast('error', 'Not connected to server');
      return;
    }
    setIsCheckingCode(true);
    setCodeCheckResult(null);
    socket.emit('admin-check-room-code', { code: normalized });

    const handler = (data: { available: boolean }) => {
      setIsCheckingCode(false);
      socket?.off('admin-check-room-code-result', handler);
      setCodeCheckResult(data.available ? 'available' : 'taken');
    };
    socket.on('admin-check-room-code-result', handler);
  }, [code, socket, showToast]);

  const handleCreatePromoRoom = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const normalized = code.trim().toUpperCase();
      if (normalized.length !== 4) {
        showToast('error', 'Room code must be 4 letters (A–Z)');
        return;
      }
      if (!isValidDuration || effectiveDuration < 1 || effectiveDuration > 90) {
        showToast('error', 'Duration must be between 1 and 90 days');
        return;
      }
      const startsAt = parseLocalDateStart(startDate);
      if (startsAt == null) {
        showToast('error', 'Choose a valid start date');
        return;
      }
      if (!socket?.connected) {
        showToast('error', 'Not connected to server');
        return;
      }
      setIsCreating(true);
      const createdGames = gamePack === 'all' ? [] : [gamePack];
      socket.emit('admin-create-promo-room', {
        code: normalized,
        durationDays: effectiveDuration,
        gamePack,
        startsAt,
      });

      const handler = (data: {
        success: boolean;
        roomName?: string;
        startsAt?: number;
        expiresAt?: number;
        error?: string;
        message?: string;
      }) => {
        setIsCreating(false);
        socket?.off('admin-create-promo-room-result', handler);
        if (data.success && data.roomName && data.expiresAt) {
          const startLabel = data.startsAt ? formatPromoDate(data.startsAt) : 'now';
          const endLabel = formatPromoDate(data.expiresAt);
          showToast(
            'success',
            `Room ${data.roomName} created. Active ${startLabel} → ${endLabel}`
          );
          setCode('');
          setStartDate(toDateInputValue());
          setCustomDuration('');
          setDurationDays(7);
          setGamePack('all');
          setCodeCheckResult(null);
          setShowAddForm(false);
          onRefresh();
          openEmailModal({ roomName: data.roomName, games: createdGames });
        } else {
          showToast('error', data.message || data.error || 'Failed to create promo room');
        }
      };
      socket.on('admin-create-promo-room-result', handler);
    },
    [code, effectiveDuration, gamePack, isValidDuration, onRefresh, openEmailModal, socket, showToast, startDate]
  );

  const handleSendPromoEmail = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!emailTarget) return;
      if (!socket?.connected) {
        showToast('error', 'Not connected to server');
        return;
      }
      const trimmedEmail = emailTo.trim();
      const trimmedName = firstName.trim();
      if (!trimmedEmail || !trimmedName) {
        showToast('error', 'Email and first name are required');
        return;
      }

      setIsSendingEmail(true);
      socket.emit('admin-send-promo-email', {
        email: trimmedEmail,
        firstName: trimmedName,
        roomName: emailTarget.roomName,
        games: emailTarget.games,
        templateId: selectedTemplate,
      });

      const handler = (data: { success: boolean; roomName?: string; error?: string; purchase?: PremiumPurchase }) => {
        setIsSendingEmail(false);
        socket?.off('admin-send-promo-email-result', handler);
        if (data.success) {
          showToast('success', `Promo email sent for ${data.roomName || emailTarget.roomName}`);
          setEmailTarget(null);
          setEmailTo('');
          setFirstName('');
          setSelectedTemplate(DEFAULT_PROMO_TEMPLATE);
          onRefresh();
        } else {
          showToast('error', data.error || 'Failed to send email');
        }
      };
      socket.on('admin-send-promo-email-result', handler);
    },
    [emailTarget, emailTo, firstName, onRefresh, selectedTemplate, socket, showToast]
  );

  const formatTimeLeft = (expiresAt: number): string => {
    const ms = Math.max(0, expiresAt - Date.now());
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    const minutes = Math.floor((ms % 3600000) / 60000);
    return minutes > 0 ? `${minutes}m` : 'Expired';
  };

  const formatPromoStatus = (purchase: PremiumPurchase): string => {
    const startsAt = purchase.startsAt ?? purchase.purchasedAt;
    if (Date.now() < startsAt) {
      return `Starts ${formatPromoDate(startsAt)}`;
    }
    return formatTimeLeft(purchase.expiresAt);
  };

  return (
    <div className="promo-rooms-page">
      {toast && (
        <div className={`promo-toast promo-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="promo-header">
        <h2 className="promo-page-title">
          <span className="title-prefix">&gt;</span> Promo Rooms
        </h2>
        <button
          className="promo-add-button"
          onClick={() => setShowAddForm((v) => !v)}
          aria-expanded={showAddForm}
        >
          {showAddForm ? 'Cancel' : 'Add room'}
        </button>
      </div>

      {showAddForm && (
        <section className="promo-add-section">
          <h3 className="promo-form-title">Create promo room</h3>
          <form onSubmit={handleCreatePromoRoom} className="promo-form">
            <div className="promo-form-row">
              <label className="promo-label">Room code (4 letters)</label>
              <div className="promo-code-input-group">
                <input
                  type="text"
                  className="promo-input promo-code-input"
                  value={code}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
                    setCode(v);
                    setCodeCheckResult(null);
                  }}
                  placeholder="ABCD"
                  maxLength={4}
                  disabled={isCreating}
                />
                <button
                  type="button"
                  className="promo-secondary-button"
                  onClick={handleGenerateRandomCode}
                  disabled={isGeneratingCode || isCreating}
                >
                  {isGeneratingCode ? '...' : 'Random'}
                </button>
                <button
                  type="button"
                  className="promo-secondary-button"
                  onClick={handleCheckCode}
                  disabled={code.length !== 4 || isCheckingCode || isCreating}
                >
                  {isCheckingCode ? '...' : 'Check'}
                </button>
              </div>
              {codeCheckResult === 'available' && (
                <span className="promo-code-status promo-code-available">✓ Available</span>
              )}
              {codeCheckResult === 'taken' && (
                <span className="promo-code-status promo-code-taken">✗ Taken</span>
              )}
              {codeCheckResult === 'invalid' && code.length > 0 && (
                <span className="promo-code-status promo-code-invalid">Must be 4 letters</span>
              )}
            </div>

            <div className="promo-form-row">
              <label className="promo-label" htmlFor="promo-start-date">
                Start date
              </label>
              <input
                id="promo-start-date"
                type="date"
                className="promo-input promo-date-input"
                value={startDate}
                min={toDateInputValue()}
                max={toDateInputValue(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000))}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isCreating}
                required
              />
              <span className="promo-field-hint">
                Access begins at local midnight on this day. Duration counts from then.
              </span>
            </div>

            <div className="promo-form-row">
              <label className="promo-label">Duration (days)</label>
              <div className="promo-duration-group">
                {DURATION_PRESETS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`promo-duration-preset ${!customDuration && durationDays === d ? 'active' : ''}`}
                    onClick={() => {
                      setDurationDays(d);
                      setCustomDuration('');
                    }}
                  >
                    {d} days
                  </button>
                ))}
                <input
                  type="number"
                  className="promo-input promo-duration-custom"
                  placeholder="Custom"
                  min={1}
                  max={90}
                  value={customDuration}
                  onChange={(e) => {
                    setCustomDuration(e.target.value);
                  }}
                />
              </div>
              {parsedStartMs != null && previewExpiresAt != null && (
                <span className="promo-field-hint">
                  Active {formatPromoDate(parsedStartMs)} → {formatPromoDate(previewExpiresAt)}
                </span>
              )}
            </div>

            <div className="promo-form-row">
              <label className="promo-label">Game pack</label>
              <select
                className="promo-select"
                value={gamePack}
                onChange={(e) => setGamePack(e.target.value as PromoGamePack)}
                disabled={isCreating}
              >
                {GAME_PACK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="promo-form-actions">
              <button
                type="button"
                className="promo-secondary-button"
                onClick={() => setShowAddForm(false)}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="promo-submit-button"
                disabled={isCreating || code.length !== 4 || !isValidDuration}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="promo-list-section">
        <h3 className="promo-list-title">Existing promo rooms ({promoRooms.length})</h3>
        {promoRooms.length === 0 ? (
          <div className="promo-empty">
            <div className="promo-empty-icon">🎫</div>
            <p className="promo-empty-text">No promo rooms yet</p>
            <p className="promo-empty-subtext">Create one above to get started</p>
          </div>
        ) : (
          <div className="promo-rooms-grid">
            {promoRooms.map((entry) => {
              const { purchase, room } = entry;
              const statusLabel = formatPromoStatus(purchase);
              const startsAt = purchase.startsAt ?? purchase.purchasedAt;
              const emailSends = purchase.promoEmailSends ?? [];
              const lastEmail = emailSends[emailSends.length - 1];
              return (
                <div
                  key={purchase.roomName}
                  className="promo-room-card promo-room-card-clickable"
                  onClick={() => onRoomClick(entry)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRoomClick(entry);
                    }
                  }}
                >
                  <div className="promo-room-card-header">
                    <span className="promo-room-code">{purchase.roomName}</span>
                    <span className="promo-room-time">{statusLabel}</span>
                  </div>
                  <div className="promo-room-card-body">
                    <div className="promo-room-pack">{purchase.packageName}</div>
                    <div className="promo-room-meta">
                      {purchase.duration} access
                      <span className="promo-room-sep">•</span>
                      <span>
                        {formatPromoDate(startsAt)} → {formatPromoDate(purchase.expiresAt)}
                      </span>
                      {room && (
                        <>
                          <span className="promo-room-sep">•</span>
                          <span>{room.players.length} player{room.players.length !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                    {lastEmail ? (
                      <div className="promo-room-email-status" title={lastEmail.email}>
                        Email sent → {lastEmail.email}
                        {emailSends.length > 1 ? ` (${emailSends.length})` : ''}
                      </div>
                    ) : (
                      <div className="promo-room-email-status muted">No email sent</div>
                    )}
                  </div>
                  <div className="promo-room-card-actions">
                    <button
                      type="button"
                      className="promo-secondary-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEmailModal({
                          roomName: purchase.roomName,
                          games: purchase.games,
                        });
                      }}
                    >
                      Send email
                    </button>
                    <span className="promo-view-details">View →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {emailTarget && (
        <div className="promo-modal-overlay" onClick={closeEmailModal} role="presentation">
          <div
            className="promo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-email-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="promo-email-title" className="promo-form-title">
              Send promo email — {emailTarget.roomName}
            </h3>
            <p className="promo-modal-hint">
              Choose which Resend template to use for this promo room email.
            </p>
            <form onSubmit={handleSendPromoEmail} className="promo-form">
              <div className="promo-form-row">
                <label className="promo-label" htmlFor="promo-email-template">
                  Template
                </label>
                <select
                  id="promo-email-template"
                  className="promo-select promo-modal-input"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  disabled={isSendingEmail}
                >
                  {PROMO_TEMPLATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="promo-field-hint">
                  {
                    PROMO_TEMPLATE_OPTIONS.find((option) => option.value === selectedTemplate)?.description
                  }
                </span>
              </div>
              <div className="promo-form-row">
                <label className="promo-label" htmlFor="promo-email-first-name">
                  First name
                </label>
                <input
                  id="promo-email-first-name"
                  type="text"
                  className="promo-input promo-modal-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Perica"
                  disabled={isSendingEmail}
                  autoFocus
                />
              </div>
              <div className="promo-form-row">
                <label className="promo-label" htmlFor="promo-email-to">
                  Recipient email
                </label>
                <input
                  id="promo-email-to"
                  type="email"
                  className="promo-input promo-modal-input"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="name@company.com"
                  disabled={isSendingEmail}
                  required
                />
              </div>
              <div className="promo-form-actions">
                <button
                  type="button"
                  className="promo-secondary-button"
                  onClick={closeEmailModal}
                  disabled={isSendingEmail}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="promo-submit-button"
                  disabled={isSendingEmail || !emailTo.trim() || !firstName.trim()}
                >
                  {isSendingEmail ? 'Sending...' : 'Send email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoRoomsPage;
